import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';

interface CancelBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  bookingId: number;
  loading?: boolean;
}

export const CancelBookingModal: React.FC<CancelBookingModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  bookingId,
  loading = false,
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const handleConfirm = () => {
    if (!reason.trim()) {
      setError('Please provide a cancellation reason');
      return;
    }

    if (reason.trim().length < 10) {
      setError('Please provide a more detailed reason (at least 10 characters)');
      return;
    }

    onConfirm(reason.trim());
    setReason('');
    setError('');
  };

  const handleClose = () => {
    setReason('');
    setError('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Cancel Booking">
      <div className="space-y-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-semibold text-yellow-900 mb-1">
                Warning: This action cannot be undone
              </h4>
              <p className="text-sm text-yellow-800">
                You are about to cancel booking #{bookingId}. This will notify both the user
                and provider about the cancellation. Please provide a clear reason for this
                action.
              </p>
            </div>
          </div>
        </div>

        <div>
          <label htmlFor="cancelReason" className="block text-sm font-medium text-gray-700 mb-2">
            Cancellation Reason <span className="text-red-500">*</span>
          </label>
          <textarea
            id="cancelReason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setError('');
            }}
            placeholder="Please provide a detailed reason for cancelling this booking..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            disabled={loading}
          />
          {error && (
            <p className="mt-2 text-sm text-red-600">{error}</p>
          )}
          <p className="mt-2 text-xs text-gray-500">
            Minimum 10 characters required. This reason will be visible to the user and provider.
          </p>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">
            What happens after cancellation?
          </h4>
          <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
            <li>The booking status will be changed to "Cancelled"</li>
            <li>Both the user and provider will be notified</li>
            <li>The cancellation reason will be recorded</li>
            <li>This action cannot be reversed</li>
          </ul>
        </div>

        <div className="flex gap-3 pt-4 border-t">
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={loading || !reason.trim()}
            className="flex-1"
          >
            {loading ? 'Cancelling...' : 'Cancel Booking'}
          </Button>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
            className="flex-1"
          >
            Keep Booking
          </Button>
        </div>
      </div>
    </Modal>
  );
};
